<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Stickers</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage sticker assets for your application</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="loading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Sticker
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-[12rem]">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search stickers..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredStickers.length }} stickers
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="loading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading stickers...</span>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="paginatedStickers.length === 0" class="bg-white rounded-xl shadow-sm py-16 px-4 text-center">
                                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <p class="text-gray-500 text-base m-0">No stickers found</p>
                                <p class="text-gray-400 text-sm mt-2 mb-0">Create a new sticker to get started</p>
                        </div>

                        <!-- Card Gallery -->
                        <div v-else>
                                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                        <div
                                                v-for="sticker in paginatedStickers"
                                                :key="sticker.id"
                                                class="group relative bg-white rounded-xl shadow-sm overflow-hidden transition-all hover:shadow-md cursor-pointer"
                                                @click="selectedSticker = sticker"
                                        >
                                                <div class="aspect-square bg-gray-50 flex items-center justify-center p-4">
                                                        <img
                                                                v-if="sticker.url"
                                                                :src="sticker.url"
                                                                alt="Sticker"
                                                                class="w-full h-full object-contain"
                                                                @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
                                                        >
                                                        <svg v-else class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                </div>
                                                <div class="px-3 py-2 border-t border-gray-100">
                                                        <p class="text-xs text-gray-500 font-mono m-0 truncate">ID: {{ sticker.id }}</p>
                                                </div>
                                                <div class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                                type="button"
                                                                class="p-1.5 bg-white rounded-lg shadow-sm text-gray-600 border-none cursor-pointer hover:text-red-600 hover:bg-red-50"
                                                                title="Delete"
                                                                @click.stop="confirmDeleteSticker(sticker)"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 mt-4 bg-white rounded-xl shadow-sm">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ startRow }} to {{ endRow }} of {{ filteredStickers.length }} stickers
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-[2.5rem]"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create New Sticker</h3>
                                        <button
                                                type="button"
                                                class="text-gray-400 bg-transparent border-none cursor-pointer p-1"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Sticker Image</label>
                                                
                                                <!-- Hidden file input -->
                                                <input
                                                        ref="fileInputRef"
                                                        type="file"
                                                        accept=".svg,image/svg+xml"
                                                        class="hidden"
                                                        @change="handleFileSelect"
                                                >

                                                <!-- Upload Zone -->
                                                <div
                                                        v-if="!newStickerUrl && !isUploading"
                                                        class="border-2 border-dashed rounded-xl py-8 px-4 text-center cursor-pointer transition-colors hover:border-orange-7 hover:bg-orange-1"
                                                        :class="isDragging ? 'border-orange-7 bg-orange-1' : 'border-gray-300'"
                                                        @click="triggerFileInput"
                                                        @dragenter.prevent="isDragging = true"
                                                        @dragover.prevent="isDragging = true"
                                                        @dragleave.prevent="isDragging = false"
                                                        @drop.prevent="handleFileDrop"
                                                >
                                                        <svg class="w-10 h-10 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <p class="text-sm text-gray-600 mb-1">
                                                                <span class="text-orange-7 font-medium">Click to upload</span> or drag and drop
                                                        </p>
                                                        <p class="text-xs text-gray-400">SVG files only</p>
                                                </div>
                                                
                                                <!-- Uploading State -->
                                                <div
                                                        v-else-if="isUploading"
                                                        class="border-2 border-dashed border-orange-7 rounded-xl py-8 px-4 text-center bg-orange-1"
                                                >
                                                        <svg class="w-10 h-10 text-orange-7 mx-auto mb-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                        </svg>
                                                        <p class="text-sm text-gray-600 mb-0">Uploading...</p>
                                                </div>
                                                
                                                <!-- Image Preview -->
                                                <div v-else class="relative border border-gray-200 rounded-xl p-4 bg-gray-50">
                                                        <div class="flex items-center gap-4">
                                                                <div class="w-20 h-20 bg-white rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 border border-gray-200">
                                                                        <img :src="newStickerUrl" alt="Preview" class="w-full h-full object-contain">
                                                                </div>
                                                                <div class="flex-1 min-w-0">
                                                                        <p class="text-sm font-medium text-gray-900 m-0">Image uploaded</p>
                                                                        <button
                                                                                type="button"
                                                                                class="text-sm text-red-600 bg-transparent border-none cursor-pointer p-0 mt-1"
                                                                                @click="newStickerUrl = ''"
                                                                        >
                                                                                Remove
                                                                        </button>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                                <div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                :disabled="!newStickerUrl || isCreating"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="createSticker"
                                        >
                                                <svg v-if="isCreating" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Preview Modal -->
                <div v-if="selectedSticker" class="fixed inset-0 z-50 flex items-center justify-center" @click="selectedSticker = null">
                        <div class="absolute inset-0 bg-black/70"></div>
                        <div class="relative max-w-2xl max-h-[80vh] p-8" @click.stop>
                                <button
                                        type="button"
                                        class="absolute top-0 right-0 text-white bg-transparent border-none cursor-pointer p-2 hover:text-gray-300"
                                        @click="selectedSticker = null"
                                >
                                        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                                <div class="bg-white rounded-xl p-6 flex items-center justify-center min-w-[20rem] min-h-[20rem] md:min-w-[28rem] md:min-h-[28rem] lg:min-w-[36rem] lg:min-h-[36rem]">
                                        <img
                                                v-if="selectedSticker.url"
                                                :src="selectedSticker.url"
                                                alt="Sticker Preview"
                                                class="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] object-contain"
                                        >
                                        <div v-else class="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] flex items-center justify-center text-gray-400">
                                                No image available
                                        </div>
                                </div>
                                <p class="text-center text-white text-sm mt-4 font-mono">ID: {{ selectedSticker.id }}</p>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { StickerModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import StickerService from 'services/StickerService';
import { httpClient } from 'utils/http';

export default defineComponent({
        name: 'StickersPage',
        setup() {
                const stickers = ref<StickerModel[]>([]);
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(25);
                const loading = ref(true);
                const showCreateModal = ref(false);
                const newStickerUrl = ref('');
                const isCreating = ref(false);
                const isDragging = ref(false);
                const isUploading = ref(false);
                const fileInputRef = ref<HTMLInputElement | null>(null);
                const selectedSticker = ref<StickerModel | null>(null);

                const filteredStickers = computed(() => {
                        if (!searchQuery.value.trim()) {
                                return stickers.value;
                        }
                        const query = searchQuery.value.toLowerCase();
                        return stickers.value.filter(sticker =>
                                String(sticker.id).includes(query) ||
                                sticker.url?.toLowerCase().includes(query)
                        );
                });

                const totalRows = computed(() => filteredStickers.value.length);
                const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
                const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
                const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

                const paginatedStickers = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredStickers.value.slice(start, start + pageSize.value);
                });

                const visiblePages = computed(() => {
                        const pages: number[] = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        const end = Math.min(totalPages.value, start + maxVisible - 1);
                        start = Math.max(1, end - maxVisible + 1);
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                watch([searchQuery], () => {
                        currentPage.value = 1;
                });

                const loadData = async () => {
                        loading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                const data = await StickerService.getAll(params);
                                stickers.value = data;
                        } catch (error: any) {
                                console.error('Failed to load stickers:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load stickers',
                                        icon: 'error',
                                });
                        } finally {
                                loading.value = false;
                        }
                };

                const confirmDeleteSticker = async (sticker: StickerModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Sticker?',
                                text: `Are you sure you want to delete sticker #${sticker.id}? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await StickerService.delete(sticker.id);
                                        stickers.value = stickers.value.filter(s => s.id !== sticker.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Sticker deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete sticker',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const triggerFileInput = () => {
                        fileInputRef.value?.click();
                };

                const handleFileSelect = async (event: Event) => {
                        const input = event.target as HTMLInputElement;
                        const file = input.files?.[0];
                        if (file) {
                                await uploadFile(file);
                        }
                };

                const handleFileDrop = async (event: DragEvent) => {
                        isDragging.value = false;
                        const file = event.dataTransfer?.files?.[0];
                        if (file) {
                                await uploadFile(file);
                        }
                };

                const uploadFile = async (file: File) => {
                        if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
                                Swal.fire({
                                        title: 'Invalid File',
                                        text: 'Please select an SVG file',
                                        icon: 'error',
                                });
                                return;
                        }

                        isUploading.value = true;
                        try {
                                const formData = new FormData();
                                formData.append('file', file);

                                const response = await httpClient.post('/api/upload', formData, {
                                        headers: {
                                                'Content-Type': 'multipart/form-data',
                                        },
                                });

                                if (response.data?.url) {
                                        newStickerUrl.value = response.data.url;
                                } else if (typeof response.data === 'string') {
                                        newStickerUrl.value = response.data;
                                } else {
                                        Swal.fire({
                                                title: 'Error',
                                                text: 'Upload succeeded but no URL returned',
                                                icon: 'error',
                                        });
                                }
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Upload Failed',
                                        text: error.response?.data?.message || error.message || 'Failed to upload file',
                                        icon: 'error',
                                });
                        } finally {
                                isUploading.value = false;
                        }
                };

                const createSticker = async () => {
                        if (!newStickerUrl.value) return;

                        isCreating.value = true;
                        try {
                                const newSticker = await StickerService.create({
                                        url: newStickerUrl.value,
                                });
                                stickers.value.unshift(newSticker);
                                showCreateModal.value = false;
                                newStickerUrl.value = '';
                                Swal.fire({
                                        toast: true,
                                        position: 'top-end',
                                        icon: 'success',
                                        title: 'Sticker created successfully',
                                        showConfirmButton: false,
                                        timer: 3000,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create sticker',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        stickers,
                        searchQuery,
                        currentPage,
                        pageSize,
                        loading,
                        showCreateModal,
                        newStickerUrl,
                        isCreating,
                        isDragging,
                        isUploading,
                        fileInputRef,
                        selectedSticker,
                        filteredStickers,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        paginatedStickers,
                        visiblePages,
                        confirmDeleteSticker,
                        triggerFileInput,
                        handleFileSelect,
                        handleFileDrop,
                        createSticker,
                        loadData,
                };
        },
});
</script>
